import { Page, PageHeader } from "../../components/layout/Page";
import {
  Alert,
  Box,
  Button,
  Drawer,
  Flex,
  Modal,
  Select,
  Skeleton,
  Table,
  Text,
  Textarea,
} from "@mantine/core";
import {
  ArrowUUpLeftIcon,
  CalendarCheckIcon,
  CheckCircleIcon,
  LockIcon,
  WarningIcon,
  XCircleIcon,
} from "@phosphor-icons/react";
import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { usePermission } from "../../components/permission";
import useSwr from "swr";
import { LedgerApi } from "../../apis";
import { ILedgerPeriod, IPeriodChecklist, LedgerPeriodStatus } from "../../apis/ledger";
import { StatusBadge } from "../../components/ui/status-badge";
import { message } from "../../utils/Message";
import grid from "../../components/ui/data-grid.module.css";
import { dateFormat } from "../../utils/date";
import shared from "../shared/DataDense.module.css";
import classes from "./ledger-table.module.css";
import journal from "./journal.module.css";
import { ListStates, resolveListStates } from "../../components/list";

const STATUS_COLOR: Record<LedgerPeriodStatus, string> = {
  OPEN   : "gray",
  CLOSING: "yellow",
  CLOSED : "gray",
  LOCKED : "gray",
};

const CURRENT_YEAR = new Date().getFullYear();
// Finding 3 (Task 12 review) — the current, still-in-progress year is never an
// option. The backend does not refuse an early close (`assertYearEndPeriodOpen`
// only checks whether a December period already exists), so the guard has to
// live here: a warning on a selectable-but-wrong option is worse than not
// offering it at all. Starts at CURRENT_YEAR - 1, the last year that has
// actually finished.
const YEAR_END_YEARS = Array.from({ length: 6 }, (_, i) => CURRENT_YEAR - 1 - i);

/**
 * Санхүүгийн мөчлөгүүд (SPRINT-05 §7 Алхам 8, Алхам 12) — мөчлөг бүрийн
 * төлөв, хаах шалгуур (7 зүйл), дахин нээх (шалтгаан заавал).
 *
 * `LOCKED` мөчлөгт дахин нээх сонголт огт харагдахгүй — `reopenPeriod` үүнийг
 * үгүйсгэдэг, зассвар дараагийн нээлттэй мөчлөгт сторно бичилтээр орно.
 */
export const LedgerPeriodsPage = () => {
  const { t } = useTranslation(["billing", "common"]);
  const canFinance = usePermission("FINANCE").hasAccess;

  const { data: periods, isLoading, error, mutate } = useSwr<ILedgerPeriod[]>(
    "ledger.periods",
    async () => await LedgerApi.getPeriods(),
    { revalidateOnFocus: false },
  );

  const [selectedId, setSelectedId] = useState<string | null>(null);
  const {
    data: checklist,
    isLoading: checklistLoading,
    error: checklistError,
    mutate: mutateChecklist,
  } = useSwr<IPeriodChecklist>(
    selectedId ? `ledger.period.checklist.${selectedId}` : null,
    async () => await LedgerApi.getPeriodChecklist(selectedId!),
    { revalidateOnFocus: false },
  );

  const selected = useMemo(() => periods?.find((p) => p.id === selectedId) ?? null, [periods, selectedId]);

  const [confirmClose, setConfirmClose] = useState(false);
  const [closing, setClosing] = useState(false);

  const [confirmReopen, setConfirmReopen] = useState(false);
  const [reopenReason, setReopenReason] = useState("");
  const [reopening, setReopening] = useState(false);

  const [yearEndOpen, setYearEndOpen] = useState(false);
  const [yearEndYear, setYearEndYear] = useState(CURRENT_YEAR - 1);
  const [yearEndBusy, setYearEndBusy] = useState(false);

  const periodLabel = (p: { year: number; month: number }) =>
    t("billing:ledger.report.periodLabel", { year: p.year, month: p.month });

  const runClose = async () => {
    if (!selected) return;
    setClosing(true);
    try {
      await LedgerApi.closePeriod(selected.id);
      message.success(t("billing:ledger.period.closeSuccess", { period: periodLabel(selected) }));
      setConfirmClose(false);
      await Promise.all([mutate(), mutateChecklist()]);
    } catch (err) {
      message.error((err as Error).message);
    } finally {
      setClosing(false);
    }
  };

  const openReopenDialog = () => {
    setReopenReason("");
    setConfirmReopen(true);
  };

  const runReopen = async () => {
    if (!selected) return;
    setReopening(true);
    try {
      await LedgerApi.reopenPeriod(selected.id, reopenReason.trim());
      message.success(t("billing:ledger.period.reopenSuccess", { period: periodLabel(selected) }));
      setConfirmReopen(false);
      await Promise.all([mutate(), mutateChecklist()]);
    } catch (err) {
      message.error((err as Error).message);
    } finally {
      setReopening(false);
    }
  };

  const runYearEnd = async () => {
    setYearEndBusy(true);
    try {
      const result = await LedgerApi.yearEndClose(yearEndYear);
      // Three outcomes, three sentences. "Already closed" used to arrive as a
      // failure reading "the business had no revenue or expense activity in
      // 2026", which sounds like data loss rather than like nothing to do.
      const key = result.closeStatus === "ALREADY_CLOSED"
        ? "billing:ledger.period.yearEndAlreadyClosed"
        : result.closeStatus === "SUPPLEMENTED"
          ? "billing:ledger.period.yearEndSupplemented"
          : "billing:ledger.period.yearEndSuccess";
      message.success(t(key, { number: result.entryNumber }));
      setYearEndOpen(false);
    } catch (err) {
      message.error((err as Error).message);
    } finally {
      setYearEndBusy(false);
    }
  };

  const orderedPeriods = useMemo(
    () => [...(periods ?? [])].sort((a, b) => a.year - b.year || a.month - b.month),
    [periods],
  );
  const now = new Date();
  const isCurrentMonth = (p: ILedgerPeriod) => p.year === now.getFullYear() && p.month === now.getMonth() + 1;
  const earliestOpen = orderedPeriods.find((p) => p.status === "OPEN" && !isCurrentMonth(p)) ?? null;
  const earliestOpenId = earliestOpen?.id ?? null;
  const periodCardLabel = (p: { year: number; month: number }) =>
    t("billing:ledger.period.cardLabel", { year: p.year, month: p.month });

  const reasonValid = reopenReason.trim().length >= 3;
  const passedCount = checklist?.items.filter((i) => i.ok).length ?? 0;

  const listStates = resolveListStates(
    <ListStates
      loading={isLoading}
      error={error}
      onRetry={() => mutate()}
      empty={!periods || periods.length === 0}
      columns={5}
    />
  );

  return (
    <Page>
      <PageHeader
        title={
          <>
            {t("billing:ledger.period.title")}
            {periods && periods.length > 0 && (
              <Text span className={journal.titleChip}>
                {t("billing:ledger.period.periodsChip", { count: periods.length })}
              </Text>
            )}
          </>
        }
        subtitle={t("billing:ledger.period.subtitle")}
        extra={
          canFinance ? (
            <Button variant="default" leftSection={<CalendarCheckIcon size={14} />} onClick={() => setYearEndOpen(true)}>
              {t("billing:ledger.period.yearEndButton")}
            </Button>
          ) : undefined
        }
      />

      {error && (
        <Alert color="red" radius="md" mb="md" icon={<WarningIcon size={16} weight="bold" />}>
          <Text size="sm" fw={500}>{t("billing:ledger.report.loadError")}</Text>
        </Alert>
      )}

      {listStates ? (
        <Box className={grid.card}>{listStates}</Box>
      ) : (
        <>
          {/* Canvas: сар бүр нэг карт, хамгийн эртнээс. Зөвхөн хамгийн эртний нээлттэй сар үндсэн «Хаах» товчтой. */}
          <Box className={classes.periodGrid}>
            {orderedPeriods.map((p) => {
              const isEarliest = p.id === earliestOpenId;
              const isCurrent = isCurrentMonth(p);
              const note = p.status === "OPEN"
                ? isCurrent
                  ? t("billing:ledger.period.currentMonth")
                  : isEarliest
                    ? t("billing:ledger.period.earliestOpen")
                    : t("billing:ledger.period.notClosed")
                : p.closedAt
                  ? t("billing:ledger.period.closedOn", { date: dateFormat(p.closedAt) })
                  : t(`billing:ledger.period.status.${p.status}`);
              return (
                <Box
                  key={p.id}
                  className={classes.periodCard}
                  data-earliest={isEarliest || undefined}
                  role="button"
                  tabIndex={0}
                  onClick={() => setSelectedId(p.id)}
                  onKeyDown={(e) => { if (e.key === "Enter") setSelectedId(p.id); }}>
                  <Box className={classes.periodHead}>
                    <Text span className={classes.periodName}>{periodCardLabel(p)}</Text>
                    <StatusBadge
                      color={STATUS_COLOR[p.status]}
                      label={t(`billing:ledger.period.status.${p.status}`)}
                    />
                  </Box>
                  <Text span className={classes.periodRange}>{p.startDate.slice(5)} - {p.endDate.slice(5)}</Text>
                  <Text span className={classes.periodNote}>{note}</Text>
                  {p.status === "OPEN" && !isCurrent && canFinance ? (
                    <Button
                      fullWidth
                      size="sm"
                      variant={isEarliest ? "filled" : "default"}
                      color={isEarliest ? "brand" : undefined}
                      onClick={(e) => { e.stopPropagation(); setSelectedId(p.id); }}>
                      {t("billing:ledger.period.closeShort")}
                    </Button>
                  ) : p.status === "CLOSED" && canFinance ? (
                    <Button fullWidth size="sm" variant="default" onClick={(e) => { e.stopPropagation(); setSelectedId(p.id); }}>
                      {t("billing:ledger.period.reopenButton")}
                    </Button>
                  ) : (
                    <Box className={classes.periodNoAction}>-</Box>
                  )}
                </Box>
              );
            })}
          </Box>

          {earliestOpen && (
            <Box className={classes.periodOrder}>
              <Text span fw={600}>{t("billing:ledger.period.closeOrderTitle")}</Text>
              <Text span>{t("billing:ledger.period.closeOrderBody", { period: periodCardLabel(earliestOpen) })}</Text>
            </Box>
          )}
        </>
      )}

      <Drawer
        opened={selectedId !== null}
        onClose={() => setSelectedId(null)}
        position="right"
        size="lg"
        title={selected ? periodLabel(selected) : ""}>
        {selected && (
          <Box>
            <Flex align="center" gap="xs" mb="md">
              <StatusBadge
                color={STATUS_COLOR[selected.status]}
                label={t(`billing:ledger.period.status.${selected.status}`)}
              />
              <Text size="sm" c="dimmed">{selected.startDate} - {selected.endDate}</Text>
            </Flex>

            {selected.status === "LOCKED" && (
              <Alert color="gray" radius="md" mb="md" icon={<LockIcon size={16} weight="bold" />}>
                {t("billing:ledger.period.lockedNote")}
              </Alert>
            )}

            {selected.status === "CLOSING" && (
              <Alert color="yellow" radius="md" mb="md" icon={<WarningIcon size={16} weight="bold" />}>
                {t("billing:ledger.period.closingNote")}
              </Alert>
            )}

            {selected.reopenReason && (
              <Alert color="gray" radius="md" mb="md" icon={<ArrowUUpLeftIcon size={16} weight="bold" />}>
                {t("billing:ledger.period.reopenedNote", {
                  reason: selected.reopenReason,
                  date  : selected.reopenedAt ? selected.reopenedAt.slice(0, 10) : "",
                })}
              </Alert>
            )}

            <Text size="sm" fw={500} c="dimmed" tt="uppercase" mb="xs">
              {t("billing:ledger.period.checklistTitle")}
            </Text>

            {checklistError && (
              <Alert color="red" radius="md" mb="md" icon={<WarningIcon size={16} weight="bold" />}>
                {t("billing:ledger.report.loadError")}
              </Alert>
            )}

            <Box className={classes.scroll} mb="md">
              {listStates ?? (
              <Table className={classes.table} highlightOnHover verticalSpacing="sm" horizontalSpacing="md" withRowBorders>
                <Table.Tbody>
                  {checklistLoading ? (
                    Array.from({ length: 7 }).map((_, i) => (
                      <Table.Tr key={`sk-${i}`}>
                        <Table.Td w={28}><Skeleton h={14} w={14} /></Table.Td>
                        <Table.Td><Skeleton h={14} /></Table.Td>
                        <Table.Td><Skeleton h={14} /></Table.Td>
                      </Table.Tr>
                    ))
                  ) : (
                    (checklist?.items ?? []).map((item) => (
                      <Table.Tr key={item.code}>
                        <Table.Td w={28}>
                          {item.ok
                            ? <CheckCircleIcon size={16} weight="bold" color="var(--mantine-color-green-6)" />
                            : <XCircleIcon size={16} weight="bold" color="var(--mantine-color-red-6)" />}
                        </Table.Td>
                        <Table.Td>
                          <Text size="sm" fw={500}>{item.label}</Text>
                        </Table.Td>
                        <Table.Td>
                          <Text size="sm" c="dimmed">{item.detail}</Text>
                        </Table.Td>
                      </Table.Tr>
                    ))
                  )}
                </Table.Tbody>
              </Table>
              )}
            </Box>

            <Flex justify="flex-end" gap="sm">
              {selected.status === "OPEN" && canFinance && (
                <Button
                  color="green"
                  leftSection={<CheckCircleIcon size={14} weight="bold" />}
                  disabled={!checklist?.canClose}
                  onClick={() => setConfirmClose(true)}>
                  {t("billing:ledger.period.closeButton")}
                </Button>
              )}
              {selected.status === "CLOSED" && canFinance && (
                <Button
                  color="red"
                  variant="light"
                  leftSection={<ArrowUUpLeftIcon size={14} weight="bold" />}
                  onClick={openReopenDialog}>
                  {t("billing:ledger.period.reopenButton")}
                </Button>
              )}
            </Flex>
          </Box>
        )}
      </Drawer>

      {/* Close confirmation — semantic colours + summary card, the app's existing pattern (journal.tsx's reverse-entry dialog). */}
      <Modal
        opened={confirmClose}
        onClose={() => setConfirmClose(false)}
        title={t("billing:ledger.period.closeConfirmTitle")}
        size="sm">
        {selected && (
          <Box>
            <Alert color="green" radius="md" icon={<CheckCircleIcon size={18} weight="bold" />} mb="md">
              {t("billing:ledger.period.closeConfirmBody")}
            </Alert>
            <Box className={shared.card} mb="md">
              <Box className={shared.filterBar}>
                <Text size="sm" c="dimmed">{t("billing:ledger.period.colPeriod")}</Text>
                <Box flex={1} />
                <Text size="sm" fw={500}>{periodLabel(selected)}</Text>
              </Box>
              <Box className={shared.filterBar}>
                <Text size="sm" c="dimmed">{t("billing:ledger.period.checklistTitle")}</Text>
                <Box flex={1} />
                <Text size="sm" fw={500}>
                  {t("billing:ledger.period.checklistPassed", { ok: passedCount, total: checklist?.items.length ?? 0 })}
                </Text>
              </Box>
            </Box>
            <Flex justify="flex-end" gap="sm">
              <Button variant="subtle" color="gray" onClick={() => setConfirmClose(false)} disabled={closing}>
                {t("common:cancel")}
              </Button>
              <Button color="green" loading={closing} onClick={runClose}>
                {t("billing:ledger.period.closeButton")}
              </Button>
            </Flex>
          </Box>
        )}
      </Modal>

      {/* Reopen confirmation — reason mandatory client-side, mirroring the server's own check (min 3 chars). */}
      <Modal
        opened={confirmReopen}
        onClose={() => setConfirmReopen(false)}
        title={t("billing:ledger.period.reopenConfirmTitle")}
        size="sm">
        {selected && (
          <Box>
            <Alert color="red" radius="md" icon={<WarningIcon size={18} weight="bold" />} mb="md">
              {t("billing:ledger.period.reopenConfirmBody")}
            </Alert>
            <Box className={shared.card} mb="md">
              <Box className={shared.filterBar}>
                <Text size="sm" c="dimmed">{t("billing:ledger.period.colPeriod")}</Text>
                <Box flex={1} />
                <Text size="sm" fw={500}>{periodLabel(selected)}</Text>
              </Box>
            </Box>
            <Textarea
              label={t("billing:ledger.period.reopenReasonLabel")}
              placeholder={t("billing:ledger.period.reopenReasonPlaceholder")}
              value={reopenReason}
              onChange={(e) => setReopenReason(e.currentTarget.value)}
              minRows={3}
              required
              mb={4}
            />
            <Text size="sm" c={reasonValid ? "dimmed" : "red.8"} mb="md">
              {t("billing:ledger.period.reopenReasonRequired")}
            </Text>
            <Flex justify="flex-end" gap="sm">
              <Button variant="subtle" color="gray" onClick={() => setConfirmReopen(false)} disabled={reopening}>
                {t("common:cancel")}
              </Button>
              <Button color="red" loading={reopening} disabled={!reasonValid} onClick={runReopen}>
                {t("billing:ledger.period.reopenButton")}
              </Button>
            </Flex>
          </Box>
        )}
      </Modal>

      {/*
        Year-end close - Finding 2 (Task 12 review): the most consequential
        action in this sprint now carries the same guardrail as the lower-
        stakes close/reopen actions above - semantic-colour Alert stating
        plainly what the entry does, then a summary card naming the year
        this run actually commits to, before the button that posts it.
      */}
      <Modal
        opened={yearEndOpen}
        onClose={() => setYearEndOpen(false)}
        title={t("billing:ledger.period.yearEndModalTitle")}
        size="sm">
        <Alert color="yellow" radius="md" icon={<WarningIcon size={18} weight="bold" />} mb="md">
          {t("billing:ledger.period.yearEndConfirmBody")}
        </Alert>
        <Select
          label={t("billing:ledger.period.yearEndYearLabel")}
          value={String(yearEndYear)}
          onChange={(value) => value && setYearEndYear(Number(value))}
          data={YEAR_END_YEARS.map((y) => ({ value: String(y), label: String(y) }))}
          mb="md"
        />
        <Box className={shared.card} mb="md">
          <Box className={shared.filterBar}>
            <Text size="sm" c="dimmed">{t("billing:ledger.period.yearEndClosingYear")}</Text>
            <Box flex={1} />
            <Text size="sm" fw={500}>{yearEndYear}</Text>
          </Box>
          <Box className={shared.filterBar}>
            <Text size="sm" c="dimmed">{t("billing:ledger.period.yearEndActionLabel")}</Text>
            <Box flex={1} />
            <Text size="sm" fw={500}>{t("billing:ledger.period.yearEndActionValue")}</Text>
          </Box>
        </Box>
        <Flex justify="flex-end" gap="sm">
          <Button variant="subtle" color="gray" onClick={() => setYearEndOpen(false)} disabled={yearEndBusy}>
            {t("common:cancel")}
          </Button>
          <Button color="brand" loading={yearEndBusy} onClick={runYearEnd}>
            {t("billing:ledger.period.yearEndConfirm")}
          </Button>
        </Flex>
      </Modal>
    </Page>
  );
};

export default LedgerPeriodsPage;
